import { useState, useEffect, useCallback } from "react";
import { useApi } from "@/hooks/useApi";
import { toast } from "sonner";

interface ColumnConfigItem {
  columnId: string;
  isVisible: boolean;
  order: number;
}

interface ColumnConfigurationResponse {
  screenName: string;
  columnId: string;
  isVisible: boolean;
  order: number;
}

export interface DisputeColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  isDefault: boolean;
  category: "basic" | "details" | "status" | "dates";
}

export const SCREEN_NAME = "Dispute_Management_All_Disputes";

export const DEFAULT_DISPUTE_COLUMNS: DisputeColumnConfig[] = [
  { id: "disputeId", label: "Dispute ID", visible: true, order: 0, isDefault: true, category: "basic" },
  { id: "customer", label: "Customer", visible: true, order: 1, isDefault: true, category: "basic" },
  { id: "type", label: "Type", visible: true, order: 2, isDefault: true, category: "details" },
  { id: "amount", label: "Amount", visible: true, order: 3, isDefault: true, category: "details" },
  { id: "status", label: "Status", visible: true, order: 4, isDefault: true, category: "status" },
  { id: "priority", label: "Priority", visible: true, order: 5, isDefault: true, category: "status" },
  { id: "workflowProgress", label: "Workflow Progress", visible: true, order: 6, isDefault: true, category: "details" },
  { id: "aiScore", label: "AI Score", visible: true, order: 7, isDefault: true, category: "details" },
  { id: "assignedTo", label: "Assigned To", visible: true, order: 8, isDefault: true, category: "basic" },
  { id: "dueDate", label: "Due Date", visible: true, order: 9, isDefault: true, category: "dates" },
  { id: "createdDate", label: "Created Date", visible: false, order: 10, isDefault: false, category: "dates" },
  { id: "workflow", label: "Workflow", visible: false, order: 11, isDefault: false, category: "details" },
  { id: "description", label: "Description", visible: false, order: 12, isDefault: false, category: "details" },
];

function mergeWithDefaults(
  apiConfigs: ColumnConfigurationResponse[],
  defaults: DisputeColumnConfig[]
): DisputeColumnConfig[] {
  const apiConfigMap = new Map(apiConfigs.map((x) => [x.columnId, x]));

  return defaults
    .map((defaultCol) => {
      const apiConfig = apiConfigMap.get(defaultCol.id);
      if (apiConfig) {
        return {
          ...defaultCol,
          visible: apiConfig.isVisible,
          order: apiConfig.order,
        };
      }
      return defaultCol;
    })
    .sort((a, b) => a.order - b.order);
}

function columnsToApiFormat(columns: DisputeColumnConfig[]): ColumnConfigItem[] {
  return columns.map((col, index) => ({
    columnId: col.id,
    isVisible: col.visible,
    order: index,
  }));
}

export function useDisputeColumnPreferences() {
  const { apiGet, apiPost } = useApi();
  const [columns, setColumns] = useState<DisputeColumnConfig[]>(DEFAULT_DISPUTE_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function loadConfig() {
      try {
        const allConfigs = await apiGet<ColumnConfigurationResponse[]>(
          "/columnConfig",
          undefined,
          controller.signal
        );
        const screenConfigs = allConfigs.filter((x) => x.screenName === SCREEN_NAME);

        if (screenConfigs.length > 0) {
          const merged = mergeWithDefaults(screenConfigs, DEFAULT_DISPUTE_COLUMNS);
          setColumns(merged);
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to load column preferences:", error);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadConfig();

    return () => controller.abort();
  }, [apiGet]);

  const savePreferences = useCallback(async (newColumns: DisputeColumnConfig[]) => {
    setIsSaving(true);
    try {
      const apiColumns = columnsToApiFormat(newColumns);
      await apiPost<void>("/columnConfig", {
        screenName: SCREEN_NAME,
        columns: apiColumns,
      });

      setColumns(newColumns);
      toast.success("Column preferences saved");
    } catch (error) {
      console.error("Failed to save column preferences:", error);
      toast.error("Failed to save column preferences");
    } finally {
      setIsSaving(false);
    }
  }, [apiPost]);

  const resetToDefaults = useCallback(async () => {
    setIsSaving(true);
    try {
      const apiColumns = columnsToApiFormat(DEFAULT_DISPUTE_COLUMNS);
      await apiPost<void>("/columnConfig", {
        screenName: SCREEN_NAME,
        columns: apiColumns,
      });

      setColumns(DEFAULT_DISPUTE_COLUMNS);
      toast.success("Reset to default columns");
    } catch (error) {
      console.error("Failed to reset column preferences:", error);
      toast.error("Failed to reset column preferences");
    } finally {
      setIsSaving(false);
    }
  }, [apiPost]);

  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  return {
    columns,
    visibleColumns,
    isLoading,
    isSaving,
    savePreferences,
    resetToDefaults,
  };
}
